import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, openPdf } from "@/lib/api";
import { PageHeader, Label2, StatusBadge, MAINT_TONE, RESULT_TONE } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, FilePdf, Play, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

function CreateDialog({ assets, onCreated }) {
  const [open, setOpen] = useState(false);
  const [assetId, setAssetId] = useState("");
  const [title, setTitle] = useState("");
  const [interval, setIntervalM] = useState("12");
  const [items, setItems] = useState([{ text: "" }]);

  const submit = async () => {
    try {
      const checklist = items.filter((i) => i.text.trim()).map((i) => ({ text: i.text.trim() }));
      await api.post("/maintenance-orders", {
        asset_id: assetId, title, interval_months: Number(interval) || 12, checklist,
      });
      toast.success("Wartungsauftrag erstellt");
      setOpen(false); setAssetId(""); setTitle(""); setItems([{ text: "" }]); onCreated();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-maintenance-button" className="gap-2"><Plus size={18} weight="bold" />Neuer Auftrag</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="maintenance-dialog">
        <DialogHeader><DialogTitle className="font-heading">Wartungsauftrag anlegen</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label2>Anlage *</Label2>
            <Select value={assetId} onValueChange={setAssetId}>
              <SelectTrigger data-testid="maintenance-asset"><SelectValue placeholder="Anlage wählen" /></SelectTrigger>
              <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label2>Titel *</Label2>
            <Input data-testid="maintenance-title" placeholder="z.B. Jahreswartung Torantrieb" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          <div className="space-y-1.5"><Label2>Intervall (Monate)</Label2>
            <Input type="number" value={interval} onChange={(e) => setIntervalM(e.target.value)} /></div>
          <div className="space-y-2">
            <Label2>Checkliste</Label2>
            {items.map((it, idx) => (
              <div key={idx} className="flex gap-2">
                <Input placeholder={`Prüfpunkt ${idx + 1}`} value={it.text}
                  onChange={(e) => setItems((p) => p.map((x, i) => i === idx ? { text: e.target.value } : x))} />
                <Button variant="ghost" size="icon" onClick={() => setItems((p) => p.filter((_, i) => i !== idx))}><Trash size={16} /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setItems((p) => [...p, { text: "" }])}><Plus size={14} />Punkt hinzufügen</Button>
          </div>
        </div>
        <DialogFooter><Button data-testid="maintenance-save" onClick={submit} disabled={!assetId || !title}>Erstellen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompleteDialog({ order, onDone }) {
  const [open, setOpen] = useState(false);
  const [checklist, setChecklist] = useState((order.checklist || []).map((c) => ({ ...c, result: c.result || "ok" })));
  const [result, setResult] = useState("erfolgreich");
  const [arbeit, setArbeit] = useState("60");
  const [anfahrt, setAnfahrt] = useState("30");
  const [note, setNote] = useState("");
  const [sig, setSig] = useState("");

  const submit = async () => {
    try {
      await api.post(`/maintenance-orders/${order.id}/complete`, {
        checklist, result, time_arbeit_min: Number(arbeit) || 0, time_anfahrt_min: Number(anfahrt) || 0,
        note, signature_name: sig,
      });
      toast.success("Wartung abgeschlossen", { description: "Protokoll wurde generiert" });
      setOpen(false); onDone();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" className="gap-1.5" data-testid={`complete-maintenance-${order.id}`}><CheckCircle size={16} weight="bold" />Abschließen</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="maintenance-complete-dialog">
        <DialogHeader><DialogTitle className="font-heading">Wartung durchführen</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label2>Checkliste</Label2>
            {checklist.length === 0 && <p className="text-sm text-muted-foreground">Keine Checkpunkte hinterlegt.</p>}
            {checklist.map((c, idx) => (
              <div key={c.id || idx} className="rounded-md border border-border p-3">
                <div className="mb-2 text-sm font-medium">{c.text}</div>
                <div className="flex gap-1.5">
                  {["ok", "mangel", "na"].map((r) => (
                    <button key={r} onClick={() => setChecklist((p) => p.map((x, i) => i === idx ? { ...x, result: r } : x))}
                      className={`rounded-md border px-3 py-1 text-xs font-semibold transition-colors ${c.result === r ? (r === "ok" ? "border-success bg-success/15 text-success" : r === "mangel" ? "border-danger bg-danger/15 text-danger" : "border-border bg-muted") : "border-border text-muted-foreground hover:bg-accent"}`}>
                      {r === "ok" ? "OK" : r === "mangel" ? "Mangel" : "N/A"}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label2>Arbeitszeit (min)</Label2><Input type="number" value={arbeit} onChange={(e) => setArbeit(e.target.value)} /></div>
            <div className="space-y-1.5"><Label2>Anfahrt (min)</Label2><Input type="number" value={anfahrt} onChange={(e) => setAnfahrt(e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label2>Ergebnis</Label2>
            <Select value={result} onValueChange={setResult}>
              <SelectTrigger data-testid="maintenance-result"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="erfolgreich">Erfolgreich</SelectItem>
                <SelectItem value="mit_maengeln">Mit Mängeln</SelectItem>
                <SelectItem value="abgebrochen">Abgebrochen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label2>Bemerkung</Label2><Textarea value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <div className="space-y-1.5"><Label2>Digitale Unterschrift (Name)</Label2><Input data-testid="maintenance-signature" value={sig} onChange={(e) => setSig(e.target.value)} placeholder="Monteur-Name" /></div>
        </div>
        <DialogFooter><Button data-testid="maintenance-complete-save" onClick={submit} disabled={!sig}>Abschließen & Protokoll erzeugen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Maintenance() {
  const [orders, setOrders] = useState(null);
  const [assets, setAssets] = useState([]);

  const load = useCallback(() => {
    api.get("/maintenance-orders").then((r) => setOrders(r.data));
    api.get("/assets").then((r) => setAssets(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const start = async (id) => { await api.post(`/maintenance-orders/${id}/start`); toast.success("Auftrag gestartet"); load(); };

  if (!orders) return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="Wartung" subtitle={`${orders.length} Wartungsaufträge · Checklisten & Zeitarten`}>
        <CreateDialog assets={assets} onCreated={load} />
      </PageHeader>
      <Card className="divide-y divide-border rounded-md border">
        {orders.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Noch keine Wartungsaufträge.</div>}
        {orders.map((m) => (
          <div key={m.id} data-testid="maintenance-row" className="flex flex-col justify-between gap-3 px-5 py-4 sm:flex-row sm:items-center">
            <div className="min-w-0">
              <div className="font-semibold">{m.title}</div>
              <div className="text-xs text-muted-foreground">{m.asset_name} · erstellt {new Date(m.created_at).toLocaleDateString("de-DE")}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {m.result && <StatusBadge tone={RESULT_TONE[m.result]}>{m.result.replace("_", " ")}</StatusBadge>}
              <StatusBadge tone={MAINT_TONE[m.status]}>{m.status}</StatusBadge>
              {m.status !== "abgeschlossen" && m.status !== "in_arbeit" && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => start(m.id)}><Play size={16} />Start</Button>}
              {m.status !== "abgeschlossen" && <CompleteDialog order={m} onDone={load} />}
              {m.status === "abgeschlossen" && <Button size="sm" variant="outline" className="gap-1.5" data-testid={`pdf-maintenance-${m.id}`} onClick={() => openPdf(`/pdf/maintenance/${m.id}`)}><FilePdf size={16} />PDF</Button>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
